<template>
  <div class="form">
    <div class="form__content" :class="nftLayoutClass">
      <p class="subheading small-headline">
        {{ $t("PageSubTitle.smallHeadline.incoming") }}..
      </p>
      <h1 class="extra-large-headline">
        {{
          globalStore.getMerchantSettings.VALIDATE_MSISDN
            ? $t("PageTitle.headline.verificationCodeCheckMobileTitle")
            : $t("PageTitle.headline.verificationCodeCheckEmailTitle")
        }}.
      </h1>
      <p
        v-html="
          $t('PageSubTitle.paragraph.verificationCodeInstructions', {
            mobileOrEmailAddress: `<b>${getOTPContact()}</b>`,
          })
        "
      />
    </div>
    <form @submit.prevent>
      <VerificationCode
        v-model:verificationCode="form.pinCode"
        :input-length="parseInt(globalStore.getMerchantSettings.otp_length)"
        :errors="verificationCodeStore.getErrorByField('pinCode')"
      />

      <!--todo renable when ready  -->
      <!-- <p class="resend-code" v-if="hasSubmittedOnce">
        Didn't get a SMS?
        <a
          ref="noreferrer noopner"
          target="_blank"
          href="#"
          @click.prevent="resendCode"
          >Resend code</a
        >
      </p> -->

      <ButtonBase
        :isDisabled="
          verificationCodeStore.formHasErrors || globalStore.getIsFormLoading
        "
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.verifyMe") }}
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { useGlobalStore } from "@/stores/globalStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useVerificationCodeStore } from "@/stores/forms/verificationCodeFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useErrorCodeCallbacks from "@/composables/errors/useErrorCodeCallbacks";
import useNumbersToWords from "@/composables/helpers/numbersToWords";

import VerificationCode from "@/components/ui/inputs/VerificationCode.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

const route = useRoute();
const router = useRouter();
const verificationStore = useVerificationStore();
const verificationCodeStore = useVerificationCodeStore();
const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();
const { postRequest } = useApiHelpers();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { useOrderChaining } = useErrorCodeCallbacks();
const { t } = useI18n();
const { convertNumberToWords } = useNumbersToWords();

const form = ref({
  pinCode: verificationCodeStore.form.pinCode.value || "",
});
const hasSubmittedOnce = ref(false);
const showResendAfterDelay = 10000;
const mobile = verificationStore.form?.mobile?.value;
const email = verificationStore.form.email.value;
const internationalCode = verificationStore.form?.internationalCode?.value;
const orderStore = useOrderFormStore();

const nftLayoutClass = computed(() =>
  orderStore.isNFTMode ? "nft-layout" : ""
);

const resendCode = () => {
  useOrderChaining({
    title: "Code resent",
    text: `Your new code is on its way to ${mobile}`,
    primaryBtnText: "Got it!",
  });
};

const handleFormRedirect = (response) => {
  const { data } = response;
  if (data.rel.route === "identification.update") {
    router.push({ name: "identity.confirm-details" });
  } else {
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  }
};

const getOTPContact = () => {
  if (globalStore.getMerchantSettings.VALIDATE_MSISDN) {
    return (
      orderStore.getActiveStore.otpData.mobile ??
      `${internationalCode} ${mobile}`
    );
  }
  return orderStore.getActiveStore.otpData.email ?? email;
};

const handleFormSubmit = () => {
  setTimeout(() => {
    hasSubmittedOnce.value = true;
  }, showResendAfterDelay);

  globalStore.setIsFormLoading(true);
  postRequest("auth", form.value, handleFormRedirect, ({ field, error }) => {
    if (field && error) {
      globalStore.setIsFormLoading(false);
      verificationCodeStore.updateErrorFieldByKey(field, error);
    }
  });
};

watchAndUpdateStore(form, verificationCodeStore);
// on order chaining need to clear
watch(
  () => verificationCodeStore.form.pinCode.value,
  (pinCode) => {
    form.value.pinCode = pinCode;
  },
  { deep: true }
);

onMounted(() => {
  document.getElementById("verificationCodeInput").focus();
});
</script>
<style lang="scss" scoped>
.subheading {
  padding-bottom: $padding-sml;
  color: var(--primary);
}
.resend-code {
  text-align: center;
  margin-bottom: $formFieldSpacing;
}
.nft-layout {
  .button {
    @include breakpoint($max-sm) {
      margin-top: 10rem;
    }
  }
}
</style>
